/**
 * Public proposal share page (v2-1-4) — route /p/:token. NO login.
 *
 * A client opens the unguessable link the operator sent them; this fetches the
 * client-facing proposal view via plain fetch (no auth) and renders it on-brand:
 * client/campaign header, intro, and each attached video playing INLINE / PDFs
 * opened in a tab. An unknown token shows a friendly 404 (not a stack trace).
 *
 * This is the ONLY public surface for a proposal — there is no gallery/browse.
 */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PdfPreview, VideoPlayer } from '../components/MediaPlayer';
import { isSafeHttpUrl } from '../lib/safeUrl';
import {
  fetchPublicProposal,
  ProposalNotFoundError,
  type PublicProposal,
} from '../lib/publicApi';

/**
 * Derive the auto-thumbnail poster URL for a public proposal video (v5-1-6).
 *
 * The public ProposalItem type carries NO poster_url field, so we derive it by
 * convention from the video URL: `…/foo.mp4` -> `…/foo-poster.jpg` (the pipeline
 * writes the poster next to the source). We only return it when:
 *   - the source is an .mp4 (the only format the poster pipeline produces), and
 *   - the derived poster URL is a safe http(s) URL (XSS/SSRF guard).
 * Otherwise we return undefined and the player simply shows no poster.
 */
function derivePosterUrl(videoUrl: string | undefined): string | undefined {
  if (!videoUrl || !/\.mp4$/i.test(videoUrl)) return undefined;
  const posterUrl = videoUrl.replace(/\.mp4$/i, '-poster.jpg');
  return isSafeHttpUrl(posterUrl) ? posterUrl : undefined;
}

type Load =
  | { phase: 'loading' }
  | { phase: 'not-found' }
  | { phase: 'error'; message: string }
  | { phase: 'ready'; data: PublicProposal };

function BrandHeader() {
  return (
    <header className="border-b border-line bg-white/80 backdrop-blur">
      <div className="mx-auto flex max-w-4xl items-center gap-2 px-4 py-4 sm:px-6">
        <span className="h-6 w-6 rounded-lg bg-aurora" aria-hidden="true" />
        <span className="font-heading text-sm font-bold text-ink">My Investments — Corporate Partnerships</span>
      </div>
    </header>
  );
}

function NotFound() {
  return (
    <div className="flex flex-1 items-center justify-center px-4 py-20">
      <div className="max-w-md text-center">
        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-aurora-soft">
          <svg viewBox="0 0 24 24" className="h-8 w-8 text-primary-600" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <circle cx="12" cy="12" r="10" />
            <path d="M9.5 9a2.5 2.5 0 0 1 5 0c0 1.5-2.5 2-2.5 3.5" />
            <path d="M12 17h.01" />
          </svg>
        </div>
        <h1 className="font-heading text-2xl font-bold text-ink">This proposal link isn’t valid</h1>
        <p className="mt-3 text-ink-500">
          The link may have expired or been mistyped. Please ask your contact to resend it.
        </p>
      </div>
    </div>
  );
}

export function ProposalSharePage() {
  const { token } = useParams<{ token: string }>();
  const [state, setState] = useState<Load>({ phase: 'loading' });

  useEffect(() => {
    let live = true;
    if (!token) {
      setState({ phase: 'not-found' });
      return;
    }
    fetchPublicProposal(token)
      .then((data) => live && setState({ phase: 'ready', data }))
      .catch((err) => {
        if (!live) return;
        if (err instanceof ProposalNotFoundError) setState({ phase: 'not-found' });
        else setState({ phase: 'error', message: err instanceof Error ? err.message : 'Something went wrong.' });
      });
    return () => {
      live = false;
    };
  }, [token]);

  return (
    <div className="flex min-h-screen flex-col bg-surface-alt">
      <BrandHeader />

      {state.phase === 'loading' && (
        <div className="flex flex-1 items-center justify-center px-4 py-20">
          <p className="text-sm text-ink-500">Loading your proposal…</p>
        </div>
      )}

      {state.phase === 'not-found' && <NotFound />}

      {state.phase === 'error' && (
        <div className="flex flex-1 items-center justify-center px-4 py-20">
          <p className="text-sm text-rose-600" role="alert">
            {state.message}
          </p>
        </div>
      )}

      {state.phase === 'ready' && (
        <main className="mx-auto w-full max-w-4xl flex-1 px-4 py-8 sm:px-6 sm:py-12">
          <div className="rounded-3xl bg-aurora p-6 text-white shadow-cta sm:p-10">
            <p className="font-heading text-xs font-semibold uppercase tracking-wider text-white/80">
              Proposal for {state.data.client_name}
            </p>
            <h1 className="mt-2 font-heading text-3xl font-bold sm:text-4xl">
              {state.data.campaign_title}
            </h1>
          </div>

          {state.data.intro_md && (
            <div className="mt-6 rounded-2xl border border-line bg-white p-5 shadow-card sm:p-6">
              {/*
                SECURITY: intro_md is rendered here as ESCAPED PLAIN TEXT (React
                text node + whitespace-pre-wrap), NOT as HTML — so operator-entered
                content cannot inject markup on this public, unauthenticated page.
                If markdown->HTML rendering is ever added, the HTML MUST be
                sanitised (e.g. DOMPurify) before dangerouslySetInnerHTML.
              */}
              <p className="whitespace-pre-wrap text-ink-700">{state.data.intro_md}</p>
            </div>
          )}

          <div className="mt-6 space-y-5">
            {state.data.items.length === 0 ? (
              <p className="text-sm text-ink-500">No items have been added to this proposal yet.</p>
            ) : (
              state.data.items.map((item, i) => (
                <section
                  key={`${item.url}-${i}`}
                  className="rounded-2xl border border-line bg-white p-4 shadow-card sm:p-5"
                >
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <h2 className="font-heading text-base font-bold text-ink">{item.label}</h2>
                    {/* Force-download via the same-origin proxy (Content-Disposition:
                        attachment) — a save dialog, not the file opening in a new tab. */}
                    <a
                      href={`/api/v1/proposals/p/${encodeURIComponent(token!)}/download/${i}`}
                      className="inline-flex shrink-0 cursor-pointer items-center gap-1.5 rounded-lg border border-line bg-white px-3 py-1.5 text-xs font-semibold text-ink-700 transition-colors duration-200 hover:bg-surface-alt focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                      aria-label={`Download ${item.label}`}
                    >
                      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <path d="M7 10l5 5 5-5" />
                        <path d="M12 15V3" />
                      </svg>
                      Download
                    </a>
                  </div>
                  {item.media_type === 'video' ? (
                    <VideoPlayer
                      src={item.url}
                      label={item.label}
                      poster={derivePosterUrl(item.url)}
                    />
                  ) : (
                    <PdfPreview src={item.url} label={item.label} withInlinePreview />
                  )}
                </section>
              ))
            )}
          </div>

          <footer className="mt-10 pb-6 text-center text-xs text-ink-500">
            Shared privately by My Investments — Corporate Partnerships.
          </footer>
        </main>
      )}
    </div>
  );
}

export default ProposalSharePage;
